import { clear, preload, suspend } from 'suspend-react';

export interface ResolverPreload {
  <TResolverInput, TResolverRestArgs extends any[], TResolverReturn>(
    resolver: (input: TResolverInput, ...restArgs: TResolverRestArgs) => Promise<TResolverReturn>,
    input: TResolverInput,
    ...restArgs: TResolverRestArgs
  ): void;
  <TResolverInput, TResolverRestArgs extends any[], TResolverReturn>(
    resolver: (input: TResolverInput, ...restArgs: TResolverRestArgs) => Promise<TResolverReturn>,
    input: TResolverInput[],
    ...restArgs: TResolverRestArgs
  ): void;
}

export interface ResolverClear {
  (keys: any[]): void;
}

export interface ResolverHook {
  <TResolverInput, TResolverRestArgs extends any[], TResolverReturn>(
    resolver: (input: TResolverInput, ...restArgs: TResolverRestArgs) => Promise<TResolverReturn>,
    input: TResolverInput,
    ...restArgs: TResolverRestArgs
  ): TResolverReturn;
  <TResolverInput, TResolverRestArgs extends any[], TResolverReturn>(
    resolver: (input: TResolverInput, ...restArgs: TResolverRestArgs) => Promise<TResolverReturn>,
    input: TResolverInput[],
    ...restArgs: TResolverRestArgs
  ): TResolverReturn[];
  preload: ResolverPreload;
  clear: ResolverClear;
}

const uniqueId = Symbol('useResolver');

export const useResolver: ResolverHook = (resolver: any, input: any, ...restArgs: any[]) => {
  const keys = (Array.isArray(input) ? input : [input]) as any[];
  const deps = [...keys, ...restArgs, uniqueId];
  const results = suspend(() => Promise.all(keys.map((key) => resolver(key, ...restArgs))), deps);

  return (Array.isArray(input) ? results : results[0]) as any;
};

useResolver.preload = (resolver: any, input: any, ...restArgs: any[]) => {
  const keys = Array.isArray(input) ? input : [input];
  const deps = [...keys, ...restArgs, uniqueId];
  return preload(() => Promise.all(keys.map((key) => resolver(key, ...restArgs))), deps);
};

useResolver.clear = (keys: any[]) => {
  const deps = [...keys, uniqueId];
  return clear(deps);
};
